export type AppLang = 'fr' | 'en';

const FRANCOPHONE_COUNTRY_CODES = new Set([
  'FR', 'BE', 'CH', 'LU', 'MC', 'CA',
  'CD', 'CG', 'CI', 'SN', 'CM', 'ML', 'BF', 'NE', 'TG', 'BJ', 'GA', 'CF', 'TD',
  'RW', 'BI', 'MG', 'HT', 'DJ', 'KM', 'SC', 'MU', 'GN', 'GQ', 'YT', 'RE', 'GP', 'MQ', 'GF', 'PF', 'NC',
]);

const FRANCOPHONE_TIMEZONES = [
  'Africa/Kinshasa',
  'Africa/Lubumbashi',
  'Africa/Brazzaville',
  'Africa/Abidjan',
  'Africa/Dakar',
  'Africa/Douala',
  'Africa/Libreville',
  'Africa/Bamako',
  'Africa/Ouagadougou',
  'Africa/Lome',
  'Africa/Porto-Novo',
  'Africa/Niamey',
  'Africa/Nouakchott',
  'Africa/Conakry',
  'Africa/Bujumbura',
  'Africa/Kigali',
  'Africa/Djibouti',
  'Africa/Antananarivo',
  'Europe/Paris',
  'Europe/Brussels',
  'Europe/Luxembourg',
  'Europe/Monaco',
  'America/Montreal',
  'America/Toronto',
  'America/Guadeloupe',
  'America/Martinique',
  'America/Cayenne',
];

export function isAppLang(value: string | null | undefined): value is AppLang {
  return value === 'fr' || value === 'en';
}

export function detectLangFromBrowser(): AppLang {
  if (typeof navigator === 'undefined') {
    return 'fr';
  }

  const candidates = [
    ...(navigator.languages ?? []),
    navigator.language,
  ].filter(Boolean);

  for (const tag of candidates) {
    const lower = String(tag).toLowerCase();
    if (lower.startsWith('fr')) {
      return 'fr';
    }
    if (lower.startsWith('en')) {
      return 'en';
    }
  }

  try {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    if (FRANCOPHONE_TIMEZONES.some((zone) => zone === timeZone)) {
      return 'fr';
    }
  } catch {
    // ignore
  }

  return 'fr';
}

export function langFromCountryCode(countryCode: string | null | undefined): AppLang | null {
  if (!countryCode) {
    return null;
  }

  const code = countryCode.trim().toUpperCase();
  if (!code) {
    return null;
  }

  return FRANCOPHONE_COUNTRY_CODES.has(code) ? 'fr' : 'en';
}

/** Best-effort country lookup (no key). Returns null on failure. */
export async function fetchVisitorCountryCode(): Promise<string | null> {
  if (typeof fetch === 'undefined') {
    return null;
  }

  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 2500);
    const response = await fetch('https://api.country.is/', {
      signal: controller.signal,
      headers: { Accept: 'application/json' },
    });
    clearTimeout(timeout);

    if (!response.ok) {
      return null;
    }

    const data = (await response.json()) as { country?: string };
    return data?.country ? String(data.country).toUpperCase() : null;
  } catch {
    return null;
  }
}

export function resolveInitialAppLang(): AppLang {
  if (typeof localStorage !== 'undefined') {
    const saved = localStorage.getItem('lang');
    if (isAppLang(saved)) {
      return saved;
    }
  }

  return detectLangFromBrowser();
}

/**
 * Language tag for API `Accept-Language` (backend: fr | en | fr-FR | en-EN).
 * Prefer the active UI lang, then persisted lang, then browser, then fr.
 */
export function resolveAcceptLanguage(preferred?: string | null): string {
  if (isAppLang(preferred)) {
    return preferred;
  }
  if (typeof localStorage !== 'undefined') {
    const saved = localStorage.getItem('lang');
    if (isAppLang(saved)) {
      return saved;
    }
  }
  return detectLangFromBrowser();
}
